<template>
  <v-dialog
    :value="value"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    content-class="ps-dialog"
    @input="$emit('input', $event)"
  >
    <div class="ps" :style="rootStyle" dir="rtl">
      <!-- Top bar -->
      <header class="ps-top">
        <span class="ps-top__icon"><v-icon size="18">mdi-printer-outline</v-icon></span>
        <div class="ps-top__titles">
          <div class="ps-top__title">{{ $t("printStudio") }}</div>
          <div class="ps-top__sub">{{ title }}</div>
        </div>

        <v-menu offset-y max-height="420">
          <template v-slot:activator="{ on, attrs }">
            <button type="button" class="ps-btn ps-btn--select" v-bind="attrs" v-on="on">
              <v-icon size="16">mdi-file-document-edit-outline</v-icon>
              <span class="ps-ellipsis">{{ tplName(tpl) }}</span>
              <v-icon v-if="isLocked" size="14" color="#e65100">mdi-lock</v-icon>
              <v-icon size="14">mdi-chevron-down</v-icon>
            </button>
          </template>
          <v-list dense class="ps-menu">
            <template v-for="g in templateGroups">
              <v-subheader v-if="g.items.length" :key="'h-' + g.key">{{ g.label }}</v-subheader>
              <v-list-item
                v-for="t in g.items"
                :key="g.key + t.id"
                link
                :input-value="tpl && tpl.id === t.id"
                @click="useTemplate(t, g.key)"
              >
                <v-list-item-title>
                  {{ tplName(t) }}
                  <v-icon v-if="company.defaultId === t.id" size="13" color="amber darken-2">mdi-star</v-icon>
                  <v-icon v-if="company.lockedIds.includes(t.id)" size="13">mdi-lock-outline</v-icon>
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
        <button
          type="button"
          class="ps-btn"
          :disabled="!canSave || storeBusy"
          :title="$t('Save')"
          @click="save()"
        >
          <v-icon size="16">mdi-content-save-outline</v-icon>
          <span class="ps-btn__text">{{ $t("Save") }}</span>
          <span v-if="dirty" class="ps-dot" />
        </button>
        <button
          type="button"
          class="ps-btn"
          :disabled="!canSaveAs || storeBusy"
          :title="$t('printSaveAs')"
          @click="saveAs()"
        >
          <v-icon size="16">mdi-content-save-plus-outline</v-icon>
          <span class="ps-btn__text">{{ $t("printSaveAs") }}</span>
        </button>

        <div class="ps-seg">
          <button
            type="button"
            :class="{ on: scope === 'page' }"
            @click="setScope('page')"
          >
            {{ $t("printScopePage") }}
            <small>{{ faNum(source.currentRows().length) }}</small>
          </button>
          <button type="button" :class="{ on: scope === 'all' }" @click="setScope('all')">
            {{ $t("printScopeAll") }}
            <small>{{ faNum(fmtInt(source.total())) }}</small>
          </button>
        </div>

        <span class="ps-spacer" />

        <button
          type="button"
          class="ps-btn ps-btn--primary"
          :disabled="fetching || printing"
          @click="print"
        >
          <v-progress-circular v-if="printing" indeterminate size="14" width="2" />
          <v-icon v-else size="16">mdi-printer</v-icon>
          {{ $t("print") }}
        </button>
        <v-menu offset-y left min-width="230">
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="ps-btn"
              :disabled="fetching || printing || !!exporting"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon size="16">mdi-export-variant</v-icon>
              <span class="ps-btn__text">{{ $t("printExport") }}</span>
              <v-icon size="14">mdi-chevron-down</v-icon>
            </button>
          </template>
          <v-list dense class="ps-menu">
            <v-list-item link @click="exportPdf(false)">
              <v-list-item-icon class="me-3"><v-icon color="#dc2626">mdi-file-pdf-box</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t("printExportPdf") }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t("printExportPdfHint") }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item link @click="exportExcel(false)">
              <v-list-item-icon class="me-3"><v-icon color="#16a34a">mdi-file-excel-box</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t("printExportExcel") }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t("printExportExcelHint") }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="my-1" />
            <v-subheader>{{ $t("printShare") }}</v-subheader>
            <v-list-item link @click="exportPdf(true)">
              <v-list-item-icon class="me-3"><v-icon>mdi-share-variant-outline</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t("printSharePdf") }}</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="exportExcel(true)">
              <v-list-item-icon class="me-3"><v-icon>mdi-share-variant-outline</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t("printShareExcel") }}</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="shareTo('telegram')">
              <v-list-item-icon class="me-3"><v-icon color="#229ED9">mdi-send-circle</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t("printShareTelegram") }}</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="shareTo('whatsapp')">
              <v-list-item-icon class="me-3"><v-icon color="#25D366">mdi-whatsapp</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t("printShareWhatsapp") }}</v-list-item-title>
            </v-list-item>
            <div v-if="!shareSupported" class="ps-menu-note">
              <v-icon size="14">mdi-information-outline</v-icon>
              {{ $t("printShareFallback") }}
            </div>
          </v-list>
        </v-menu>
        <button type="button" class="ps-btn ps-btn--icon" :title="$t('close')" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </header>

      <!-- Template storage errors (403 / 404 / 422 messages from the server) -->
      <div v-if="storeError" class="ps-progress ps-progress--warn">
        <v-icon size="16">mdi-alert-circle-outline</v-icon>
        <span>{{ storeError }}</span>
        <button type="button" class="ps-link" @click="storeError = ''">{{ $t("close") }}</button>
      </div>
      <div v-else-if="storeBusy" class="ps-progress">
        <v-progress-circular indeterminate size="14" width="2" color="primary" />
        <span>{{ $t("printSyncing") }}</span>
      </div>

      <!-- Export progress -->
      <div v-if="exporting" class="ps-progress">
        <v-progress-circular indeterminate size="14" width="2" color="primary" />
        <div class="ps-progress__bar"><span :style="{ width: exportPct + '%' }" /></div>
        <span>{{ exporting }}</span>
        <button type="button" class="ps-link" @click="exportCancelled = true">{{ $t("cancel") }}</button>
      </div>
      <div v-if="notice" class="ps-progress ps-progress--ok">
        <v-icon size="16">mdi-check-circle-outline</v-icon>
        <span>{{ notice }}</span>
        <button type="button" class="ps-link" @click="notice = ''">{{ $t("close") }}</button>
      </div>

      <!-- Fetch progress -->
      <div v-if="fetching" class="ps-progress">
        <div class="ps-progress__bar"><span :style="{ width: progressPct + '%' }" /></div>
        <span>
          {{ $t("printFetching") }}
          {{ faNum(fmtInt(progress.loaded)) }} / {{ faNum(fmtInt(progress.total)) }}
          ({{ faNum(progress.page) }}/{{ faNum(progress.lastPage) }})
        </span>
        <button type="button" class="ps-link" @click="cancelFetch">{{ $t("cancel") }}</button>
      </div>
      <div v-else-if="truncated" class="ps-progress ps-progress--warn">
        <v-icon size="16">mdi-alert-outline</v-icon>
        {{ $t("printTruncated", { n: faNum(fmtInt(rows.length)) }) }}
      </div>

      <div class="ps-main">
        <!-- Settings panel -->
        <aside class="ps-side">
          <nav class="ps-tabs">
            <button
              v-for="tb in tabs"
              :key="tb.key"
              type="button"
              :class="{ on: tab === tb.key }"
              :title="tb.label"
              @click="tab = tb.key"
            >
              <v-icon size="18">{{ tb.icon }}</v-icon>
              <span>{{ tb.label }}</span>
            </button>
          </nav>

          <div v-if="tpl" class="ps-panel" :class="{ 'ps-panel--ro': readOnly && tab !== 'admin' }">
            <div v-if="readOnly && tab !== 'admin'" class="ps-note">
              <v-icon size="15">mdi-lock-outline</v-icon>
              {{ $t("printLockedNote") }}
            </div>

            <!-- Paper -->
            <template v-if="tab === 'paper'">
              <div class="ps-group">
                <div class="ps-label">{{ $t("printPaper") }}</div>
                <div class="ps-chips">
                  <button
                    v-for="s in paperSizes"
                    :key="s"
                    type="button"
                    :class="{ on: tpl.page.size === s }"
                    @click="tpl.page.size = s"
                  >
                    {{ paperLabel(s) }}
                  </button>
                </div>
                <div v-if="tpl.page.size === 'custom'" class="ps-row2">
                  <label class="ps-field">
                    <span>{{ $t("width") }} (mm)</span>
                    <input v-model.number="tpl.page.customW" type="number" min="50" />
                  </label>
                  <label class="ps-field">
                    <span>{{ $t("height") }} (mm)</span>
                    <input v-model.number="tpl.page.customH" type="number" min="50" />
                  </label>
                </div>
              </div>
              <div v-if="!isRollPaper" class="ps-group">
                <div class="ps-label">{{ $t("printOrientation") }}</div>
                <div class="ps-orient">
                  <button
                    type="button"
                    :class="{ on: tpl.page.orientation === 'portrait' }"
                    @click="tpl.page.orientation = 'portrait'"
                  >
                    <span class="ps-sheet" />{{ $t("printPortrait") }}
                  </button>
                  <button
                    type="button"
                    :class="{ on: tpl.page.orientation === 'landscape' }"
                    @click="tpl.page.orientation = 'landscape'"
                  >
                    <span class="ps-sheet ps-sheet--land" />{{ $t("printLandscape") }}
                  </button>
                </div>
              </div>
              <div class="ps-group">
                <div class="ps-label">{{ $t("printMargins") }} (mm)</div>
                <div class="ps-row4">
                  <label v-for="m in marginKeys" :key="m.key" class="ps-field">
                    <span>{{ m.label }}</span>
                    <input v-model.number="tpl.page.margins[m.key]" type="number" min="0" max="40" />
                  </label>
                </div>
              </div>
              <div class="ps-group">
                <div class="ps-label">{{ $t("printDensity") }}</div>
                <div class="ps-seg ps-seg--full">
                  <button
                    v-for="d in densities"
                    :key="d.key"
                    type="button"
                    :class="{ on: tpl.theme.density === d.key }"
                    @click="tpl.theme.density = d.key"
                  >
                    {{ d.label }}
                  </button>
                </div>
              </div>
            </template>

            <!-- Columns -->
            <template v-else-if="tab === 'columns'">
              <div v-if="options.length" class="ps-group">
                <div class="ps-label">{{ $t("printOptions") }}</div>
                <div class="ps-grid2">
                  <label v-for="o in options" :key="o.key" class="ps-toggle">
                    <input v-model="tpl.options[o.key]" type="checkbox" /><i />
                    {{ o.label }}
                  </label>
                </div>
              </div>
              <div class="ps-group">
                <div class="ps-label ps-label--row">
                  {{ $t("printColumns") }}
                  <span class="ps-muted">{{ $t("printColumnsHint") }}</span>
                </div>
                <div class="ps-cols">
                  <div
                    v-for="(c, idx) in tpl.columns"
                    :key="c.key"
                    class="ps-col"
                    :class="{ off: !c.visible, over: dragOver === idx }"
                    draggable="true"
                    @dragstart="dragFrom = idx"
                    @dragover.prevent="dragOver = idx"
                    @dragleave="dragOver = null"
                    @drop.prevent="dropCol(idx)"
                  >
                    <v-icon size="16" class="ps-col__grip">mdi-drag-vertical</v-icon>
                    <button type="button" class="ps-check" :class="{ on: c.visible }" @click="c.visible = !c.visible">
                      <v-icon size="14">mdi-check</v-icon>
                    </button>
                    <input
                      v-model="c.label"
                      class="ps-col__label"
                      :placeholder="colDef(c.key).label"
                    />
                    <button
                      type="button"
                      class="ps-icon ps-icon--sm"
                      :title="$t('printAlign') + ': ' + alignLabel(c.align)"
                      @click="c.align = nextAlign(c.align)"
                    >
                      <v-icon size="15">{{ alignIcon(c.align) }}</v-icon>
                    </button>
                    <input
                      v-model.number="c.width"
                      class="ps-col__width"
                      type="range"
                      min="0.4"
                      max="4"
                      step="0.1"
                      :title="$t('width')"
                    />
                  </div>
                </div>
              </div>
              <div class="ps-group">
                <label class="ps-toggle">
                  <input v-model="tpl.showRowNumber" type="checkbox" /><i />
                  {{ $t("printRowNumber") }}
                </label>
              </div>
              <div class="ps-group">
                <div class="ps-label">{{ $t("printTotals") }}</div>
                <label v-for="o in totalToggles" :key="o.key" class="ps-toggle ps-toggle--hint">
                  <input v-model="tpl.totals[o.key]" type="checkbox" /><i />
                  <span>{{ o.label }}<small>{{ o.hint }}</small></span>
                </label>
              </div>
            </template>

            <!-- Style -->
            <template v-else-if="tab === 'style'">
              <div class="ps-group">
                <div class="ps-label">{{ $t("printTablePresets") }}</div>
                <div class="ps-gallery">
                  <button
                    v-for="pr in tablePresets"
                    :key="pr.id"
                    type="button"
                    class="ps-card"
                    @click="applyTable(pr.id)"
                  >
                    <span class="ps-mini-table" :class="'ps-mini-table--' + pr.id" :style="{ '--a': tpl.theme.accent }">
                      <i /><i /><i /><i />
                    </span>
                    <span>{{ $t(pr.name) }}</span>
                  </button>
                </div>
              </div>
              <div class="ps-group">
                <div class="ps-label">{{ $t("printAccent") }}</div>
                <div class="ps-swatches">
                  <button
                    v-for="c in swatches"
                    :key="c"
                    type="button"
                    :class="{ on: tpl.theme.accent === c }"
                    :style="{ background: c }"
                    @click="tpl.theme.accent = c"
                  />
                  <input v-model="tpl.theme.accent" type="color" class="ps-color" />
                </div>
              </div>
              <div class="ps-group">
                <div class="ps-label">{{ $t("printHeaderStyle") }}</div>
                <div class="ps-seg ps-seg--full">
                  <button
                    v-for="h in headerStyles"
                    :key="h.key"
                    type="button"
                    :class="{ on: tpl.theme.headerStyle === h.key }"
                    @click="tpl.theme.headerStyle = h.key"
                  >
                    {{ h.label }}
                  </button>
                </div>
                <div class="ps-label">{{ $t("printBorders") }}</div>
                <div class="ps-seg ps-seg--full">
                  <button
                    v-for="b in borderStyles"
                    :key="b.key"
                    type="button"
                    :class="{ on: tpl.theme.borders === b.key }"
                    @click="tpl.theme.borders = b.key"
                  >
                    {{ b.label }}
                  </button>
                </div>
              </div>
              <div class="ps-group">
                <div class="ps-label">{{ $t("printCellAlign") }}</div>
                <div class="ps-seg ps-seg--full">
                  <button
                    v-for="a in alignOptions"
                    :key="'t' + a.key"
                    type="button"
                    :class="{ on: tpl.theme.cellAlign === a.key }"
                    :title="a.label"
                    @click="tpl.theme.cellAlign = a.key"
                  >
                    <v-icon size="16">{{ a.icon }}</v-icon>
                  </button>
                </div>
                <div class="ps-label">{{ $t("printNumberAlign") }}</div>
                <div class="ps-seg ps-seg--full">
                  <button
                    v-for="a in alignOptions"
                    :key="'n' + a.key"
                    type="button"
                    :class="{ on: tpl.theme.numberAlign === a.key }"
                    :title="a.label"
                    @click="tpl.theme.numberAlign = a.key"
                  >
                    <v-icon size="16">{{ a.icon }}</v-icon>
                  </button>
                </div>
                <div class="ps-muted ps-small">{{ $t("printAlignHint") }}</div>
              </div>
              <div class="ps-group">
                <div class="ps-label ps-label--row">
                  {{ $t("printFontSize") }}
                  <b>{{ faNum(tpl.theme.fontSize) }}pt</b>
                </div>
                <input v-model.number="tpl.theme.fontSize" type="range" min="6" max="14" step="0.5" class="ps-range" />
                <div class="ps-label">{{ $t("printFont") }}</div>
                <div class="ps-fonts">
                  <button
                    v-for="f in fonts"
                    :key="f.id"
                    type="button"
                    :class="{ on: tpl.theme.fontId === f.id }"
                    @click="tpl.theme.fontId = f.id"
                  >
                    {{ f.label }}
                  </button>
                </div>
              </div>
              <div class="ps-group">
                <div class="ps-label">{{ $t("printDirection") }}</div>
                <div class="ps-seg ps-seg--full">
                  <button
                    v-for="d in directions"
                    :key="d.key"
                    type="button"
                    :class="{ on: tpl.direction === d.key }"
                    @click="tpl.direction = d.key"
                  >
                    {{ d.label }}
                  </button>
                </div>
              </div>
              <div class="ps-group">
                <label class="ps-toggle">
                  <input v-model="tpl.theme.zebra" type="checkbox" /><i />
                  {{ $t("printZebra") }}
                </label>
                <label class="ps-toggle">
                  <input v-model="tpl.theme.faDigits" type="checkbox" /><i />
                  {{ $t("printFaDigits") }}
                </label>
                <label class="ps-field">
                  <span>{{ $t("printWatermark") }}</span>
                  <input v-model="tpl.watermark" type="text" />
                </label>
              </div>
            </template>
            <!-- Header / footer -->
            <template v-else-if="tab === 'header'">
              <div class="ps-group">
                <label class="ps-toggle ps-toggle--head">
                  <input v-model="tpl.header.show" type="checkbox" /><i />
                  {{ $t("printHeader") }}
                </label>
                <template v-if="tpl.header.show">
                  <div class="ps-gallery">
                    <button
                      v-for="pr in headerPresets"
                      :key="pr.id"
                      type="button"
                      class="ps-card"
                      @click="applyHeader(pr.id)"
                    >
                      <span class="ps-mini-head" :class="'ps-mini-head--' + pr.patch.style" :style="{ '--a': tpl.theme.accent }">
                        <b v-for="k in ['start', 'center', 'end']" :key="k" :class="'s-' + k">
                          <i v-if="pr.patch.slots.logo === k" class="logo" />
                          <i v-if="pr.patch.slots.company === k" class="co" />
                          <i v-if="pr.patch.slots.title === k" class="ti" />
                          <i v-if="pr.patch.slots.meta === k" class="me" />
                        </b>
                      </span>
                      <span>{{ $t(pr.name) }}</span>
                    </button>
                  </div>
                  <div class="ps-label">{{ $t("printPositions") }}</div>
                  <div v-for="el in headerElements" :key="el.key" class="ps-slotrow">
                    <span>{{ el.label }}</span>
                    <div class="ps-seg">
                      <button
                        v-for="sl in slotOptions"
                        :key="sl.key"
                        type="button"
                        :class="{ on: tpl.header.slots[el.key] === sl.key }"
                        :title="sl.label"
                        @click="tpl.header.slots[el.key] = sl.key"
                      >
                        <v-icon size="15">{{ sl.icon }}</v-icon>
                      </button>
                    </div>
                  </div>
                  <div class="ps-label">{{ $t("printHeaderFrame") }}</div>
                  <div class="ps-seg ps-seg--full">
                    <button
                      v-for="st in headerFrames"
                      :key="st.key"
                      type="button"
                      :class="{ on: tpl.header.style === st.key }"
                      @click="tpl.header.style = st.key"
                    >
                      {{ st.label }}
                    </button>
                  </div>
                  <label class="ps-field">
                    <span>{{ $t("printTitle") }}</span>
                    <input v-model="tpl.header.title" type="text" :placeholder="title" />
                  </label>
                  <label class="ps-field">
                    <span>{{ $t("printSubtitle") }}</span>
                    <input v-model="tpl.header.subtitle" type="text" :placeholder="subtitle || $t('printSubtitleAuto')" />
                  </label>
                  <div class="ps-grid2">
                    <label v-for="o in headerToggles" :key="o.key" class="ps-toggle">
                      <input v-model="tpl.header[o.key]" type="checkbox" /><i />
                      {{ o.label }}
                    </label>
                  </div>
                  <template v-if="extraCtx && extraCtx.parties">
                    <div class="ps-label">{{ $t("printParties") }}</div>
                    <div class="ps-grid2">
                      <label class="ps-toggle">
                        <input v-model="tpl.parties.seller" type="checkbox" /><i />
                        {{ $t("printSellerInfo") }}
                      </label>
                      <label class="ps-toggle">
                        <input v-model="tpl.parties.buyer" type="checkbox" /><i />
                        {{ $t("printBuyerInfo") }}
                      </label>
                    </div>
                  </template>
                  <div class="ps-label ps-label--row">
                    {{ $t("printHeaderHeight") }}
                    <b>{{ faNum(tpl.header.height) }}mm</b>
                  </div>
                  <input v-model.number="tpl.header.height" type="range" min="10" max="40" class="ps-range" />
                </template>
              </div>
              <div class="ps-group">
                <label class="ps-toggle ps-toggle--head">
                  <input v-model="tpl.footer.show" type="checkbox" /><i />
                  {{ $t("printFooter") }}
                </label>
                <template v-if="tpl.footer.show">
                  <div class="ps-chips">
                    <button
                      v-for="pr in footerPresets"
                      :key="pr.id"
                      type="button"
                      @click="applyFooter(pr.id)"
                    >
                      {{ $t(pr.name) }}
                    </button>
                  </div>
                  <div v-for="el in footerElements" :key="el.key" class="ps-slotrow">
                    <span>{{ el.label }}</span>
                    <div class="ps-seg">
                      <button
                        v-for="sl in slotOptions"
                        :key="sl.key"
                        type="button"
                        :class="{ on: tpl.footer.slots[el.key] === sl.key }"
                        :title="sl.label"
                        @click="tpl.footer.slots[el.key] = sl.key"
                      >
                        <v-icon size="15">{{ sl.icon }}</v-icon>
                      </button>
                    </div>
                  </div>
                  <label class="ps-field">
                    <span>{{ $t("printFooterText") }}</span>
                    <input v-model="tpl.footer.text" type="text" />
                  </label>
                </template>
                <label class="ps-toggle">
                  <input v-model="tpl.footer.signatures" type="checkbox" /><i />
                  {{ $t("printSignatures") }}
                </label>
                <label v-if="tpl.footer.signatures" class="ps-field">
                  <span>{{ $t("printSignatureLabels") }}</span>
                  <input v-model="signatureText" type="text" />
                </label>
              </div>
            </template>
            <!-- Admin -->
            <template v-else-if="tab === 'admin' && isAdmin">
              <div class="ps-group">
                <div class="ps-label">{{ $t("printCompanyTemplates") }}</div>
                <div v-if="!companyTemplates.length" class="ps-muted">{{ $t("printNoCompanyTemplates") }}</div>
                <div v-for="t in companyTemplates" :key="t.id" class="ps-admin-row">
                  <span class="ps-ellipsis">{{ tplName(t) }}</span>
                  <button
                    type="button"
                    class="ps-icon"
                    :class="{ on: company.defaultId === t.id }"
                    :title="$t('printSetDefault')"
                    @click="setDefault(t.id)"
                  >
                    <v-icon size="16">{{ company.defaultId === t.id ? "mdi-star" : "mdi-star-outline" }}</v-icon>
                  </button>
                  <button
                    type="button"
                    class="ps-icon"
                    :class="{ on: company.lockedIds.includes(t.id) }"
                    :title="$t('printLock')"
                    @click="toggleLock(t.id)"
                  >
                    <v-icon size="16">
                      {{ company.lockedIds.includes(t.id) ? "mdi-lock" : "mdi-lock-open-variant-outline" }}
                    </v-icon>
                  </button>
                  <button type="button" class="ps-icon ps-icon--danger" :title="$t('remove')" @click="removeCompany(t.id)">
                    <v-icon size="16">mdi-trash-can-outline</v-icon>
                  </button>
                </div>
                <button type="button" class="ps-btn ps-btn--block" @click="publish">
                  <v-icon size="16">mdi-domain</v-icon>
                  {{ $t("printPublish") }}
                </button>
              </div>
              <div class="ps-group">
                <div class="ps-label">{{ $t("printUserAccess") }}</div>
                <label class="ps-toggle ps-toggle--hint">
                  <input v-model="company.allowUserTemplates" type="checkbox" @change="saveCompany" /><i />
                  <span>
                    {{ $t("printAllowUserTemplates") }}
                    <small>{{ $t("printAllowUserTemplatesHint") }}</small>
                  </span>
                </label>
                <div v-if="storeCtx.source === 'local'" class="ps-note ps-note--info">
                  <v-icon size="15">mdi-information-outline</v-icon>
                  {{ $t("printStorageNote") }}
                </div>
              </div>
            </template>
          </div>
        </aside>

        <!-- Preview -->
        <section ref="stage" class="ps-stage" @wheel="onWheel">
          <div class="ps-stage__bar">
            <span>
              {{ $t("printPages") }}: <b>{{ faNum(pageCount) }}</b>
              <template v-if="pageCount > previewPages">
                · {{ $t("printPreviewOf", { n: faNum(previewPages) }) }}
              </template>
              · {{ $t("printRows") }}: <b>{{ faNum(fmtInt(rows.length)) }}</b>
            </span>
            <span class="ps-spacer" />
            <button type="button" class="ps-icon" @click="zoomBy(-0.1)"><v-icon size="16">mdi-minus</v-icon></button>
            <span class="ps-zoom">{{ faNum(Math.round(zoom * 100)) }}٪</span>
            <button type="button" class="ps-icon" @click="zoomBy(0.1)"><v-icon size="16">mdi-plus</v-icon></button>
            <button type="button" class="ps-icon" :title="$t('printFit')" @click="fit"><v-icon size="16">mdi-fit-to-page-outline</v-icon></button>
          </div>
          <iframe ref="frame" class="ps-frame" :srcdoc="previewHtml" title="preview" @load="onFrameLoad" />
        </section>
      </div>

      <!-- Name / confirm dialog (replaces window.prompt / confirm) -->
      <v-dialog v-model="nd.open" max-width="440" content-class="ps-nd-wrap" @keydown.esc="ndClose(null)">
        <div class="ps-nd" :style="rootStyle" dir="rtl">
          <div class="ps-nd__head">
            <span class="ps-nd__icon" :class="{ 'ps-nd__icon--danger': nd.danger }">
              <v-icon size="20">{{ nd.icon }}</v-icon>
            </span>
            <div>
              <div class="ps-nd__title">{{ nd.title }}</div>
              <div v-if="nd.text" class="ps-nd__text">{{ nd.text }}</div>
            </div>
            <button type="button" class="ps-icon ps-nd__x" :title="$t('close')" @click="ndClose(null)">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>

          <form v-if="nd.mode === 'name'" class="ps-nd__body" @submit.prevent="ndSubmit">
            <label class="ps-nd__field" :class="{ 'is-error': ndError }">
              <span class="ps-nd__label">
                {{ $t("printTemplateName") }}
                <small>{{ faNum(nd.name.length) }}/{{ faNum(60) }}</small>
              </span>
              <input
                ref="ndInput"
                v-model="nd.name"
                type="text"
                maxlength="60"
                :placeholder="$t('printTemplateNameHint')"
                autocomplete="off"
              />
              <span v-if="ndError" class="ps-nd__error">{{ ndError }}</span>
              <span v-else-if="ndDuplicate" class="ps-nd__warn">
                <v-icon size="14">mdi-alert-outline</v-icon>{{ $t("printNameDuplicate") }}
              </span>
            </label>

            <div v-if="nd.scopes.length > 1" class="ps-nd__scopes">
              <button
                v-for="sc in nd.scopes"
                :key="sc.key"
                type="button"
                class="ps-nd__scope"
                :class="{ on: nd.scope === sc.key }"
                @click="nd.scope = sc.key"
              >
                <v-icon size="20">{{ sc.icon }}</v-icon>
                <span>
                  <b>{{ sc.label }}</b>
                  <small>{{ sc.hint }}</small>
                </span>
                <i class="ps-nd__radio" />
              </button>
            </div>

            <div v-if="tpl" class="ps-nd__summary">
              <span><v-icon size="14">mdi-file-outline</v-icon>{{ tpl.page.size === "custom" ? $t("custom") : tpl.page.size }}</span>
              <span>
                <v-icon size="14">{{ tpl.page.orientation === "landscape" ? "mdi-crop-landscape" : "mdi-crop-portrait" }}</v-icon>
                {{ tpl.page.orientation === "landscape" ? $t("printLandscape") : $t("printPortrait") }}
              </span>
              <span>
                <v-icon size="14">mdi-view-column-outline</v-icon>
                {{ faNum(tpl.columns.filter(function (c) { return c.visible; }).length) }} {{ $t("printTabColumns") }}
              </span>
              <span><i class="ps-nd__swatch" :style="{ background: tpl.theme.accent }" />{{ fontLabel }}</span>
            </div>
            <button type="submit" hidden />
          </form>

          <div class="ps-nd__foot">
            <button
              type="button"
              class="ps-btn"
              :class="nd.danger ? 'ps-btn--danger' : 'ps-btn--primary'"
              :disabled="nd.mode === 'name' && !!ndError"
              @click="ndSubmit"
            >
              <v-icon size="16">{{ nd.danger ? "mdi-trash-can-outline" : "mdi-check" }}</v-icon>
              {{ nd.okLabel }}
            </button>
            <button type="button" class="ps-btn" @click="ndClose(null)">{{ $t("cancel") }}</button>
          </div>
        </div>
      </v-dialog>
    </div>
  </v-dialog>
</template>

<script>
import { renderList } from "./renderList";
import { fetchAll } from "./fetchAll";
import { PAPERS, paperSize } from "./paper";
import {
  builtInListTemplates,
  syncColumns,
  normalize,
  HEADER_PRESETS,
  FOOTER_PRESETS,
  TABLE_PRESETS,
  applyHeaderPreset,
  applyFooterPreset,
  applyTablePreset,
} from "./templates";
import { FONTS } from "./fonts";
import { toExcel, toPdf, download, shareFile, canShareFiles, safeFilename } from "./exporters";

import * as store from "./storage";

var MM = 3.7795; // px per mm at 96dpi

export default {
  name: "PrintStudio",
  props: {
    value: { type: Boolean, default: false },
    // Unique report key, e.g. "accounting-docs" (templates are stored per report).
    report: { type: String, required: true },
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    // [{ key, label, format, value(row), sum, width, defaultVisible }]
    columns: { type: Array, required: true },
    // { currentRows(): [], total(): n, fetchPage(page, perPage): Promise<{rows,lastPage,total}> }
    source: { type: Object, required: true },
    // Report's own ready-made templates (default: the list presets).
    builtIns: { type: Function, default: null },
    // Template id to open with (overrides "last used"), e.g. a choice made before opening.
    preferTemplate: { type: String, default: "" },
    // Extra render context merged into ctx (invoice parties, summary, note …).
    extraCtx: { type: Object, default: null },
    // Report toggles stored in the template: [{ key, label, default }]
    // (e.g. invoice: product code, serials, currency in column titles …).
    options: { type: Array, default: () => [] },
    // Messenger share (Telegram / WhatsApp): message text and an optional link.
    shareText: { type: String, default: "" },
    shareUrl: { type: String, default: "" },
    // Several documents in one print (e.g. group invoice print): [{ rows, ctx }].
    // Each one is rendered on its own pages, in this order.
    batch: { type: Array, default: null },
  },
  data: () => ({
    tpl: null,
    tplScope: "builtin",
    savedJson: "",
    tab: "paper",
    scope: "page",
    rows: [],
    allRows: null,
    truncated: false,
    fetching: false,
    progress: { loaded: 0, total: 0, page: 0, lastPage: 0 },
    fetcher: null,
    printing: false,
    storeCtx: { source: "server", report: "" },
    canManage: false,
    storeBusy: false,
    storeError: "",
    exporting: "",
    exportPct: 0,
    exportCancelled: false,
    notice: "",
    previewHtml: "",
    pageCount: 1,
    previewPages: 4,
    zoom: 0.8,
    renderTimer: null,
    dragFrom: null,
    nd: { open: false, mode: "name", title: "", text: "", icon: "", name: "", scope: "user", scopes: [], okLabel: "", danger: false, touched: false, resolve: null },
    dragOver: null,
    company: { defaultId: null, lockedIds: [], allowUserTemplates: true },
    userTemplates: [],
    companyTemplates: [],
  }),
  computed: {
    rootStyle() {
      var theme = this.$vuetify.theme;
      var t = theme.themes[theme.isDark ? "dark" : "light"] || {};
      var p = typeof t.primary === "string" ? t.primary : "#1976d2";
      return { "--ps-primary": p, "--ps-primary-soft": p + "1a" };
    },
    // Template manager: `can_manage` from the server (admin, owner or the
    // printTemplatesManage permission). Local-only reports fall back to admin.
    isAdmin() {
      return this.canManage;
    },
    userTemplatesAllowed() {
      return this.company.allowUserTemplates !== false || this.isAdmin;
    },
    isLocked() {
      return !!(this.tpl && this.company.lockedIds.indexOf(this.tpl.id) > -1);
    },
    readOnly() {
      return this.isLocked && !this.isAdmin;
    },
    dirty() {
      return this.tpl && JSON.stringify(this.tpl) !== this.savedJson;
    },
    canSave() {
      if (!this.tpl || !this.dirty || this.readOnly) return false;
      if (this.tplScope === "user") return true;
      return this.tplScope === "company" && this.isAdmin;
    },
    canSaveAs() {
      return !!this.tpl && this.userTemplatesAllowed;
    },
    templateGroups() {
      return [
        { key: "company", label: this.$t("printGroupCompany"), items: this.companyTemplates },
        { key: "user", label: this.$t("printGroupMine"), items: this.userTemplatesAllowed ? this.userTemplates : [] },
        { key: "builtin", label: this.$t("printGroupBuiltIn"), items: this.builtInTemplates() },
      ];
    },
    tabs() {
      var t = [
        { key: "paper", icon: "mdi-file-outline", label: this.$t("printTabPaper") },
        { key: "columns", icon: "mdi-view-column-outline", label: this.$t("printTabColumns") },
        { key: "style", icon: "mdi-palette-outline", label: this.$t("printTabStyle") },
        { key: "header", icon: "mdi-page-layout-header-footer", label: this.$t("printTabHeader") },
      ];
      if (this.isAdmin) t.push({ key: "admin", icon: "mdi-shield-account-outline", label: this.$t("printTabAdmin") });
      return t;
    },
    paperSizes() {
      return Object.keys(PAPERS).concat(["custom"]);
    },
    marginKeys() {
      return [
        { key: "top", label: this.$t("printTop") },
        { key: "bottom", label: this.$t("printBottom") },
        { key: "right", label: this.$t("printRight") },
        { key: "left", label: this.$t("printLeft") },
      ];
    },
    densities() {
      return [
        { key: "compact", label: this.$t("printCompact") },
        { key: "normal", label: this.$t("printNormal") },
        { key: "comfortable", label: this.$t("printComfortable") },
      ];
    },
    headerStyles() {
      return [
        { key: "solid", label: this.$t("printSolid") },
        { key: "soft", label: this.$t("printSoft") },
        { key: "none", label: this.$t("printPlain") },
      ];
    },
    borderStyles() {
      return [
        { key: "grid", label: this.$t("printGrid") },
        { key: "horizontal", label: this.$t("printHorizontal") },
        { key: "none", label: this.$t("printNoBorder") },
      ];
    },
    aligns() {
      return [
        { key: "right", icon: "mdi-format-align-right" },
        { key: "center", icon: "mdi-format-align-center" },
        { key: "left", icon: "mdi-format-align-left" },
      ];
    },
    headerToggles() {
      return [
        { key: "repeat", label: this.$t("printRepeatHeader") },
        { key: "showSubtitle", label: this.$t("printSubtitle") },
        { key: "showDate", label: this.$t("printDate") },
        { key: "showUser", label: this.$t("printUser") },
        { key: "showCount", label: this.$t("printCount") },
      ];
    },
    swatches() {
      return ["#1976d2", "#0f766e", "#7c3aed", "#c2185b", "#e65100", "#111827", "#475569"];
    },
    fonts() {
      return FONTS;
    },
    headerPresets() {
      return HEADER_PRESETS;
    },
    footerPresets() {
      return FOOTER_PRESETS;
    },
    tablePresets() {
      return TABLE_PRESETS;
    },
    isRtl() {
      var d = this.tpl ? this.tpl.direction : "auto";
      return d === "auto" ? !!this.$vuetify.rtl : d === "rtl";
    },
    alignOptions() {
      return [
        { key: "start", icon: this.isRtl ? "mdi-format-align-right" : "mdi-format-align-left", label: this.$t("printAlignStart") },
        { key: "center", icon: "mdi-format-align-center", label: this.$t("printAlignCenter") },
        { key: "end", icon: this.isRtl ? "mdi-format-align-left" : "mdi-format-align-right", label: this.$t("printAlignEnd") },
      ];
    },
    slotOptions() {
      return this.alignOptions.concat([{ key: "hide", icon: "mdi-eye-off-outline", label: this.$t("printHide") }]);
    },
    directions() {
      return [
        { key: "auto", label: this.$t("printDirAuto") },
        { key: "rtl", label: this.$t("printDirRtl") },
        { key: "ltr", label: this.$t("printDirLtr") },
      ];
    },
    headerElements() {
      return [
        { key: "logo", label: this.$t("printLogo") },
        { key: "company", label: this.$t("printCompany") },
        { key: "title", label: this.$t("printTitle") },
        { key: "meta", label: this.$t("printMeta") },
      ];
    },
    footerElements() {
      return [
        { key: "text", label: this.$t("printFooterText") },
        { key: "date", label: this.$t("printDate") },
        { key: "page", label: this.$t("printPageNumbers") },
      ];
    },
    headerFrames() {
      return [
        { key: "line", label: this.$t("printFrameLine") },
        { key: "band", label: this.$t("printFrameBand") },
        { key: "boxed", label: this.$t("printFrameBoxed") },
        { key: "plain", label: this.$t("printPlain") },
      ];
    },
    totalToggles() {
      return [
        { key: "page", label: this.$t("printPageTotal"), hint: this.$t("printPageTotalHint") },
        { key: "cumulative", label: this.$t("printCumulative"), hint: this.$t("printCumulativeHint") },
        { key: "carry", label: this.$t("printCarry"), hint: this.$t("printCarryHint") },
        { key: "grand", label: this.$t("printGrandTotal"), hint: this.$t("printGrandTotalHint") },
      ];
    },
    shareSupported() {
      return canShareFiles();
    },
    fileBase() {
      var title = (this.tpl && this.tpl.header.title) || this.title || "report";
      var d = new Date();
      var stamp = d.getFullYear() + "-" + (d.getMonth() + 1) + "-" + d.getDate();
      return safeFilename(title + "-" + stamp);
    },
    fontLabel() {
      var f = FONTS.find((x) => x.id === this.tpl.theme.fontId);
      return f ? f.label : "";
    },
    ndError() {
      if (this.nd.mode !== "name" || !this.nd.touched) return "";
      return this.nd.name.trim() ? "" : this.$t("is-required");
    },
    ndDuplicate() {
      var name = this.nd.name.trim();
      if (!name) return false;
      var list = this.nd.scope === "company" ? this.companyTemplates : this.userTemplates;
      return list.some(function (t) { return t.name === name; });
    },
    signatureText: {
      get() {
        return (this.tpl.footer.signatureLabels || []).join("، ");
      },
      set(v) {
        this.tpl.footer.signatureLabels = v.split(/[،,]/).map(function (s) { return s.trim(); }).filter(Boolean);
      },
    },
    progressPct() {
      return this.progress.total ? Math.min(100, (this.progress.loaded / this.progress.total) * 100) : 0;
    },
    ctx() {
      var s = this.$store.state;
      return Object.assign({
        rtl: !!this.$vuetify.rtl,
        title: this.title,
        subtitle: this.subtitle,
        company: s.abrName,
        logo: s.logo && (s.logo.link || s.logo),
        user: s.full_name,
        printedAt: this.$moment ? this.$moment().format(s.date_locale === "fa,en" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm") : new Date().toLocaleString(),
        labels: {
          printedAt: this.$t("printDate"),
          user: this.$t("printUser"),
          count: this.$t("printCount"),
          page: this.$t("printPage"),
          of: this.$t("of"),
          pageTotal: this.$t("printPageTotal"),
          carry: this.$t("printCarry"),
          grandTotal: this.$t("printGrandTotal"),
          cumulative: this.$t("printCumulative"),
          row: this.$t("row"),
        },
      }, this.extraCtx || {});
    },
    isRollPaper() {
      return !!(this.tpl && PAPERS[this.tpl.page.size] && PAPERS[this.tpl.page.size].roll);
    },
  },
  watch: {
    value(open) {
      if (open) this.open();
      else this.cancelFetch();
    },
    tpl: {
      deep: true,
      handler() {
        this.scheduleRender();
      },
    },
    rows() {
      this.scheduleRender();
    },
    // Report data that arrives after the studio opened (e.g. company stamp /
    // signature images loaded async) must refresh the preview too.
    extraCtx() {
      if (this.tpl) this.scheduleRender();
    },
    batch() {
      if (this.tpl) this.scheduleRender();
    },
  },
  mounted() {
    window.addEventListener("resize", this.fit);
    // Re-fit when the stage gets its real size (the dialog animates in, so
    // its width is 0 at open time and the preview used to start at 30%).
    if ("ResizeObserver" in window && this.$refs.stage) {
      this.stageObserver = new ResizeObserver(() => {
        var w = this.$refs.stage ? this.$refs.stage.clientWidth : 0;
        if (w && w !== this.stageWidth) {
          this.stageWidth = w;
          if (!this.userZoomed) this.fit();
        }
      });
      this.stageObserver.observe(this.$refs.stage);
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.fit);
    if (this.stageObserver) this.stageObserver.disconnect();
    this.cancelFetch();
  },
  methods: {
    faNum(v) {
      var s = String(v == null ? "" : v);
      return String(this.$i18n && this.$i18n.locale).indexOf("fa") === 0
        ? s.replace(/[0-9]/g, function (d) { return "۰۱۲۳۴۵۶۷۸۹"[d]; })
        : s;
    },
    fmtInt(n) {
      return String(Number(n) || 0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    builtInTemplates() {
      return this.builtIns ? this.builtIns() : builtInListTemplates();
    },
    preferBuiltIn() {
      var id = this.preferTemplate;
      return id ? this.builtInTemplates().find(function (t) { return t.id === id; }) : null;
    },
    paperLabel(s) {
      if (s === "custom") return this.$t("custom");
      return PAPERS[s] && PAPERS[s].roll ? this.$t("printRoll", { w: this.faNum(PAPERS[s].w) }) : s;
    },
    tplName(t) {
      if (!t) return "";
      return t.builtIn ? this.$t(t.name) : t.name || this.$t("printUntitled");
    },
    colDef(key) {
      return this.columns.find(function (c) { return c.key === key; }) || { label: key };
    },
    reloadStores() {
      var app = this;
      app.storeBusy = true;
      return store
        .load(app, app.report, app.$store.state.admin === "active")
        .then(function (r) {
          app.storeCtx = { source: r.source, report: app.report };
          app.canManage = r.canManage;
          app.company = r.settings;
          app.userTemplates = r.user;
          app.companyTemplates = r.company;
        })
        .catch(function (err) {
          // 403 (no permission for this report) or network: built-ins still work.
          app.storeCtx = { source: "server", report: app.report };
          app.canManage = false;
          app.company = { defaultId: null, lockedIds: [], allowUserTemplates: false };
          app.userTemplates = [];
          app.companyTemplates = [];
          app.storeError = store.errorMessage(err) || app.$t("printLoadError");
        })
        .finally(function () {
          app.storeBusy = false;
        });
    },
    open() {
      var app = this;
      app.storeError = "";
      app.scope = "page";
      app.allRows = null;
      app.truncated = false;
      app.rows = app.source.currentRows().slice();
      // Show a built-in right away, then switch once the server list arrives.
      if (!app.tpl) app.useTemplate(app.preferBuiltIn() || app.builtInTemplates()[0], "builtin", true);
      app.userZoomed = false;
      app.$nextTick(app.fit);
      setTimeout(app.fit, 350); // after the open transition
      app.reloadStores().then(function () {
        if (app.dirty) return;
        var last = store.lastUsed(app.report);
        var all = [].concat(
          app.companyTemplates.map(function (t) { return { t: t, s: "company" }; }),
          (app.userTemplatesAllowed ? app.userTemplates : []).map(function (t) { return { t: t, s: "user" }; }),
          app.builtInTemplates().map(function (t) { return { t: t, s: "builtin" }; })
        );
        var pick =
          (app.preferTemplate && all.find(function (x) { return String(x.t.id) === String(app.preferTemplate); })) ||
          all.find(function (x) { return String(x.t.id) === String(last); }) ||
          all.find(function (x) { return x.s === "company" && x.t.id === app.company.defaultId; }) ||
          all[0];
        app.useTemplate(pick.t, pick.s);
      });
    },
    useTemplate(t, scope) {
      var copy = normalize(JSON.parse(JSON.stringify(t)));
      copy.id = t.id;
      copy.name = t.name;
      copy.builtIn = !!t.builtIn;
      syncColumns(copy, this.columns);
      copy.options = copy.options || {};
      this.options.forEach(function (o) {
        if (copy.options[o.key] === undefined) copy.options[o.key] = !!o.default;
      });
      this.tpl = copy;
      this.tplScope = scope;
      this.savedJson = JSON.stringify(copy);
      if (!arguments[2]) store.lastUsed(this.report, t.id);
    },
    // Runs a storage call with busy state + error banner. Resolves the result or null.
    storeCall(promise) {
      var app = this;
      app.storeBusy = true;
      app.storeError = "";
      return promise
        .catch(function (err) {
          app.storeError = store.errorMessage(err) || app.$t("printSaveError");
          return null;
        })
        .finally(function () {
          app.storeBusy = false;
        });
    },
    async save() {
      if (!this.canSave) return;
      var scope = this.tplScope;
      var saved = await this.storeCall(store.save(this, this.storeCtx, scope, this.tpl));
      if (!saved) return;
      await this.reloadStores();
      this.useTemplate(saved, scope);
      this.notice = this.$t("opertationSuccess");
    },
    // ---- name / confirm dialog ----
    askName(opts) {
      var app = this;
      return new Promise(function (resolve) {
        app.nd = Object.assign(
          { open: true, mode: "name", text: "", scope: "user", scopes: [], danger: false, touched: false },
          opts,
          { resolve: resolve }
        );
        setTimeout(function () {
          var el = app.$refs.ndInput;
          if (el) {
            el.focus();
            el.select();
          }
        }, 150);
      });
    },
    askConfirm(opts) {
      var app = this;
      return new Promise(function (resolve) {
        app.nd = Object.assign(
          { open: true, mode: "confirm", name: "", scopes: [], touched: false, icon: "mdi-help-circle-outline" },
          opts,
          { resolve: resolve }
        );
      });
    },
    ndSubmit() {
      if (this.nd.mode === "name") {
        this.nd.touched = true;
        if (this.ndError) return;
        this.ndClose({ name: this.nd.name.trim(), scope: this.nd.scope });
      } else this.ndClose(true);
    },
    ndClose(result) {
      var r = this.nd.resolve;
      this.nd.open = false;
      this.nd.resolve = null;
      if (r) r(result);
    },
    async saveAs() {
      var scopes = [
        { key: "user", icon: "mdi-account-outline", label: this.$t("printScopeUser"), hint: this.$t("printScopeUserHint") },
      ];
      if (this.isAdmin) {
        scopes.push({ key: "company", icon: "mdi-domain", label: this.$t("printScopeCompany"), hint: this.$t("printScopeCompanyHint") });
      }
      var res = await this.askName({
        title: this.$t("printSaveAs"),
        text: this.$t("printSaveAsText"),
        icon: "mdi-content-save-plus-outline",
        name: this.tplName(this.tpl) + " - " + this.$t("copy"),
        okLabel: this.$t("Save"),
        scope: "user",
        scopes: scopes,
      });
      if (!res) return;
      var copy = JSON.parse(JSON.stringify(this.tpl));
      copy.id = null; // always a new template
      copy.name = res.name;
      copy.builtIn = false;
      var saved = await this.storeCall(store.save(this, this.storeCtx, res.scope, copy));
      if (!saved) return;
      await this.reloadStores();
      this.useTemplate(saved, res.scope);
      this.notice = this.$t("opertationSuccess");
    },
    // ---- admin ----
    async saveCompany() {
      var res = await this.storeCall(store.saveSettings(this, this.storeCtx, this.company));
      if (res) this.company = res;
      else await this.reloadStores(); // revert UI to what the server has
    },
    async publish() {
      var res = await this.askName({
        title: this.$t("printPublish"),
        text: this.$t("printPublishText"),
        icon: "mdi-domain",
        name: this.tplName(this.tpl),
        okLabel: this.$t("printPublishOk"),
        scope: "company",
      });
      if (!res) return;
      var name = res.name;
      var copy = JSON.parse(JSON.stringify(this.tpl));
      // Re-publishing a company template updates it; anything else is new.
      copy.id = this.tplScope === "company" ? copy.id : null;
      copy.name = name;
      copy.builtIn = false;
      var saved = await this.storeCall(store.save(this, this.storeCtx, "company", copy));
      if (!saved) return;
      await this.reloadStores();
      this.useTemplate(saved, "company");
      this.notice = this.$t("opertationSuccess");
    },
    setDefault(id) {
      this.company.defaultId = this.company.defaultId === id ? null : id;
      this.saveCompany();
    },
    toggleLock(id) {
      var i = this.company.lockedIds.indexOf(id);
      if (i === -1) this.company.lockedIds.push(id);
      else this.company.lockedIds.splice(i, 1);
      this.saveCompany();
    },
    async removeCompany(id) {
      var t = this.companyTemplates.find(function (x) { return x.id === id; });
      var ok = await this.askConfirm({
        title: this.$t("printDeleteTemplate"),
        text: (t ? "«" + this.tplName(t) + "» — " : "") + this.$t("printDeleteText"),
        icon: "mdi-trash-can-outline",
        okLabel: this.$t("remove"),
        danger: true,
      });
      if (!ok) return;
      var done = await this.storeCall(store.remove(this, this.storeCtx, "company", id).then(function () { return true; }));
      if (!done) return;
      // Server clears default/locked for the deleted template; local mode does it here.
      if (this.storeCtx.source === "local") {
        if (this.company.defaultId === id) this.company.defaultId = null;
        this.company.lockedIds = this.company.lockedIds.filter(function (x) { return x !== id; });
        await this.saveCompany();
      }
      await this.reloadStores();
      if (this.tpl && this.tpl.id === id) this.useTemplate(this.builtInTemplates()[0], "builtin");
    },
    applyHeader(id) {
      applyHeaderPreset(this.tpl, id);
    },
    applyFooter(id) {
      applyFooterPreset(this.tpl, id);
    },
    applyTable(id) {
      applyTablePreset(this.tpl, id);
    },
    nextAlign(a) {
      var order = ["auto", "start", "center", "end"];
      return order[(order.indexOf(a || "auto") + 1) % order.length];
    },
    alignIcon(a) {
      if (a === "center") return "mdi-format-align-center";
      if (a === "start") return this.isRtl ? "mdi-format-align-right" : "mdi-format-align-left";
      if (a === "end") return this.isRtl ? "mdi-format-align-left" : "mdi-format-align-right";
      return "mdi-format-align-justify";
    },
    alignLabel(a) {
      if (!a || a === "auto") return this.$t("printDirAuto");
      return this.$t("printAlign" + a.charAt(0).toUpperCase() + a.slice(1));
    },
    // ---- columns ----
    dropCol(to) {
      var from = this.dragFrom;
      this.dragOver = null;
      this.dragFrom = null;
      if (from === null || from === to) return;
      var cols = this.tpl.columns.slice();
      var moved = cols.splice(from, 1)[0];
      cols.splice(to, 0, moved);
      this.tpl.columns = cols;
    },
    // ---- data ----
    setScope(s) {
      this.scope = s;
      if (s === "page") {
        this.cancelFetch();
        this.rows = this.source.currentRows().slice();
        this.truncated = false;
        return;
      }
      if (this.allRows) {
        this.rows = this.allRows;
        return;
      }
      this.startFetch();
    },
    startFetch() {
      var app = this;
      app.fetching = true;
      app.progress = { loaded: 0, total: app.source.total(), page: 0, lastPage: 0 };
      app.fetcher = fetchAll(app.source, {
        perPage: 500,
        onProgress: function (p) {
          app.progress = p;
        },
      });
      app.fetcher.promise
        .then(function (res) {
          app.allRows = res.rows;
          app.truncated = res.truncated;
          if (app.scope === "all") app.rows = res.rows;
        })
        .catch(function (err) {
          if (err && err.message === "cancelled") return;
          app.scope = "page";
          app.$emit("error", err);
        })
        .finally(function () {
          app.fetching = false;
          app.fetcher = null;
        });
    },
    cancelFetch() {
      if (this.fetcher) this.fetcher.cancel();
      this.fetching = false;
      if (this.scope === "all" && !this.allRows) {
        this.scope = "page";
        this.rows = this.source.currentRows().slice();
      }
    },
    // ---- render ----
    // One document, or a batch: each entry rendered alone (own pages, own
    // totals), then the pages are joined into one HTML document.
    renderDoc(maxPages) {
      var app = this;
      if (!app.batch || !app.batch.length) {
        return renderList({ template: app.tpl, columns: app.columns, rows: app.rows, ctx: app.ctx, maxPages: maxPages });
      }
      var head = "";
      var bodies = [];
      var count = 0;
      app.batch.forEach(function (b) {
        var over = maxPages && count >= maxPages; // past the preview: count pages only
        var r = renderList({
          template: app.tpl,
          columns: app.columns,
          rows: b.rows,
          ctx: Object.assign({}, app.ctx, b.ctx),
          maxPages: maxPages ? Math.max(1, maxPages - count) : 0,
        });
        if (over) {
          count += r.pageCount;
          return;
        }
        var i = r.html.indexOf("<body>");
        if (!head) head = r.html.slice(0, i + 6);
        bodies.push(r.html.slice(i + 6, r.html.lastIndexOf("</body>")));
        count += r.pageCount;
      });
      return { html: head + bodies.join("") + "</body></html>", pageCount: count };
    },
    scheduleRender() {
      clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(this.render, 120);
    },
    render() {
      if (!this.tpl) return;
      var r = this.renderDoc(this.previewPages);
      this.pageCount = r.pageCount;
      this.previewHtml = r.html.replace(
        "</style>",
        "body{padding:8mm 0;zoom:" + this.zoom + "}.pm-page{box-shadow:0 2px 10px rgba(15,23,42,.15)}</style>"
      );
    },
    fit() {
      var stage = this.$refs.stage;
      if (!stage || !this.tpl || stage.clientWidth < 100) return;
      this.userZoomed = false;
      var w = paperSize(this.tpl.page).w * MM + 48;
      this.setZoom(Math.min(1.25, Math.floor(((stage.clientWidth - 32) / w) * 20) / 20));
    },
    zoomBy(d) {
      this.userZoomed = true;
      this.setZoom(Math.round((this.zoom + d) * 100) / 100);
    },
    // Zoom without rebuilding the preview (keeps the scroll position).
    setZoom(z) {
      z = Math.max(0.25, Math.min(3, z));
      var doc = this.$refs.frame && this.$refs.frame.contentDocument;
      var root = doc && doc.scrollingElement;
      var ratio = root && root.scrollHeight ? root.scrollTop / root.scrollHeight : 0;
      this.zoom = z;
      if (doc && doc.body) {
        doc.body.style.zoom = z;
        if (root) root.scrollTop = ratio * root.scrollHeight;
      } else this.render();
    },
    onFrameLoad() {
      var doc = this.$refs.frame && this.$refs.frame.contentDocument;
      if (!doc) return;
      if (doc.body) doc.body.style.zoom = this.zoom;
      doc.addEventListener("wheel", this.onWheel, { passive: false });
      doc.addEventListener("keydown", this.onFrameKey);
    },
    // Ctrl/Cmd + wheel over the preview zooms the preview, not the page.
    onWheel(e) {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      this.zoomBy(e.deltaY < 0 ? 0.1 : -0.1);
    },
    onFrameKey(e) {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === "=" || e.key === "+") {
        e.preventDefault();
        this.zoomBy(0.1);
      } else if (e.key === "-") {
        e.preventDefault();
        this.zoomBy(-0.1);
      } else if (e.key === "0") {
        e.preventDefault();
        this.fit();
      }
    },
    // ---- export ----
    async deliver(blob, filename, share) {
      if (share) {
        var shared = await shareFile(blob, filename, this.title);
        if (shared) return;
        download(blob, filename);
        this.notice = this.$t("printShareFallbackDone");
        return;
      }
      download(blob, filename);
      this.notice = this.$t("printExportDone", { name: filename });
    },
    async exportExcel(share) {
      this.notice = "";
      try {
        var blob = toExcel({ template: this.tpl, columns: this.columns, rows: this.rows, ctx: this.ctx });
        await this.deliver(blob, this.fileBase + ".xls", share);
      } catch (err) {
        this.$emit("error", err);
      }
    },
    // Telegram / WhatsApp. Phones: the system share sheet with the PDF (pick the
    // app there). Desktop: the PDF is downloaded and the messenger opens with
    // the text + link, so the file can be attached in the chat.
    async shareTo(app) {
      var text = this.shareText || this.title;
      var url = this.shareUrl;
      var msg = [text, url].filter(Boolean).join(String.fromCharCode(10));
      var pdf = null;
      try {
        pdf = await this.buildPdf();
      } catch (err) {
        if (!(err && err.message === "cancelled")) this.$emit("error", err);
        return;
      }
      var name = this.fileBase + ".pdf";
      if (await shareFile(pdf, name, text)) return;
      download(pdf, name);
      var link = app === "telegram"
        ? "https://t.me/share/url?url=" + encodeURIComponent(url || " ") + "&text=" + encodeURIComponent(text)
        : "https://wa.me/?text=" + encodeURIComponent(msg);
      window.open(link, "_blank", "noopener");
      this.notice = this.$t("printShareMessengerDone", { name: name });
    },
    async buildPdf() {
      var app = this;
      app.notice = "";
      app.exportCancelled = false;
      app.exportPct = 0;
      app.exporting = app.$t("printBuildingPdf");
      try {
        var r = app.renderDoc();
        return await toPdf({
          html: r.html,
          page: app.tpl.page,
          isCancelled: function () { return app.exportCancelled; },
          onProgress: function (i, n) {
            app.exportPct = (i / n) * 100;
            app.exporting = app.$t("printBuildingPdfPage", { i: app.faNum(i), n: app.faNum(n) });
          },
        });
      } finally {
        app.exporting = "";
      }
    },
    async exportPdf(share) {
      var app = this;
      app.notice = "";
      app.exportCancelled = false;
      app.exportPct = 0;
      app.exporting = app.$t("printBuildingPdf");
      try {
        var r = app.renderDoc();
        var blob = await toPdf({
          html: r.html,
          page: app.tpl.page,
          isCancelled: function () { return app.exportCancelled; },
          onProgress: function (i, n) {
            app.exportPct = (i / n) * 100;
            app.exporting = app.$t("printBuildingPdfPage", { i: app.faNum(i), n: app.faNum(n) });
          },
        });
        await app.deliver(blob, app.fileBase + ".pdf", share);
      } catch (err) {
        if (!(err && err.message === "cancelled")) app.$emit("error", err);
      } finally {
        app.exporting = "";
      }
    },
    print() {
      var app = this;
      app.printing = true;
      setTimeout(function () {
        var r = app.renderDoc();
        var frame = document.createElement("iframe");
        frame.style.cssText = "position:fixed;width:0;height:0;border:0;left:-9999px";
        document.body.appendChild(frame);
        var done = false;
        var finish = function () {
          if (done) return;
          done = true;
          setTimeout(function () {
            frame.contentWindow.focus();
            frame.contentWindow.print();
            app.printing = false;
            setTimeout(function () { frame.remove(); }, 1500);
          }, 250);
        };
        var doc = frame.contentDocument;
        doc.open();
        doc.write(r.html);
        doc.close();
        // Wait for the embedded fonts (and logo) before printing.
        if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(finish);
        frame.onload = finish;
        setTimeout(finish, 4000);

      }, 30);
    },
    async close() {
      if (this.dirty && this.canSave) {
        var ok = await this.askConfirm({
          title: this.$t("printUnsavedTitle"),
          text: this.$t("printUnsaved"),
          icon: "mdi-content-save-alert-outline",
          okLabel: this.$t("printCloseAnyway"),
          danger: true,
        });
        if (!ok) return;
      }
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.ps {
  --ps-bg: #eef1f6;
  --ps-surface: #fff;
  --ps-surface-2: #f8fafc;
  --ps-border: #e2e8f0;
  --ps-border-2: #cbd5e1;
  --ps-text: #0f172a;
  --ps-text-2: #334155;
  --ps-muted: #64748b;
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--ps-bg);
  color: var(--ps-text);
  font-size: 12px;
}
.ps .v-icon {
  color: inherit;
}
.ps-muted {
  color: var(--ps-muted);
  font-weight: 400;
}
.ps-spacer {
  flex: 1 1 auto;
}
.ps-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Top bar */
.ps-top {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--ps-surface);
  border-bottom: 1px solid var(--ps-border);
}
.ps-top__icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: var(--ps-primary-soft);
  color: var(--ps-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ps-top__titles {
  margin-inline-end: 8px;
  line-height: 1.3;
}
.ps-top__title {
  font-weight: 700;
  font-size: 13px;
}
.ps-top__sub {
  font-size: 11px;
  color: var(--ps-muted);
}
.ps-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--ps-border-2);
  border-radius: 6px;
  background: var(--ps-surface);
  color: var(--ps-text-2);
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.ps-btn:hover:not(:disabled) {
  border-color: var(--ps-primary);
  color: var(--ps-primary);
}
.ps-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.ps-btn--icon {
  width: 32px;
  padding: 0;
}
.ps-btn--select {
  max-width: 230px;
}
.ps-btn--primary {
  background: var(--ps-primary);
  border-color: var(--ps-primary);
  color: #fff;
  font-weight: 600;
  padding: 0 14px;
}
.ps-btn--primary:hover:not(:disabled) {
  color: #fff;
  filter: brightness(1.08);
}
.ps-btn--block {
  width: 100%;
  margin-top: 8px;
}
.ps-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f59e0b;
}
.ps-seg {
  display: inline-flex;
  height: 32px;
  border: 1px solid var(--ps-border-2);
  border-radius: 6px;
  overflow: hidden;
  background: var(--ps-surface);
}
.ps-seg button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0 10px;
  border: 0;
  background: transparent;
  color: var(--ps-text-2);
  font-size: 12px;
  cursor: pointer;
}
.ps-seg button + button {
  border-inline-start: 1px solid var(--ps-border);
}
.ps-seg button.on {
  background: var(--ps-primary-soft);
  color: var(--ps-primary);
  font-weight: 600;
}
.ps-seg small {
  font-size: 10.5px;
  opacity: 0.75;
}
.ps-seg--full {
  display: flex;
  width: 100%;
}
.ps-seg--full button {
  flex: 1;
}
.ps-link {
  border: 0;
  background: transparent;
  color: var(--ps-primary);
  cursor: pointer;
  font-size: 12px;
}

/* Progress */
.ps-progress {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px;
  background: var(--ps-surface);
  border-bottom: 1px solid var(--ps-border);
  font-size: 11.5px;
  color: var(--ps-text-2);
}
.ps-progress__bar {
  width: 220px;
  height: 6px;
  border-radius: 6px;
  background: var(--ps-primary-soft);
  overflow: hidden;
}
.ps-progress__bar span {
  display: block;
  height: 100%;
  background: var(--ps-primary);
  transition: width 0.25s;
}
.ps-progress--warn {
  color: #b45309;
  background: #fffbeb;
}

/* Layout */
.ps-main {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
}
.ps-side {
  flex: 0 0 360px;
  display: flex;
  background: var(--ps-surface);
  border-inline-end: 1px solid var(--ps-border);
  min-height: 0;
}
.ps-tabs {
  flex: 0 0 72px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 6px;
  border-inline-end: 1px solid var(--ps-border);
  background: var(--ps-surface-2);
}
.ps-tabs button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 8px 2px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--ps-muted);
  font-size: 10.5px;
  cursor: pointer;
}
.ps-tabs button:hover {
  color: var(--ps-text);
}
.ps-tabs button.on {
  background: var(--ps-primary-soft);
  color: var(--ps-primary);
  font-weight: 600;
}
.ps-panel {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 12px 16px;
}
.ps-panel--ro .ps-group {
  pointer-events: none;
  opacity: 0.55;
}
.ps-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0 12px;
  border-bottom: 1px solid var(--ps-border);
}
.ps-group:last-child {
  border-bottom: 0;
}
.ps-label {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--ps-text-2);
}
.ps-label--row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.ps-note {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 9px;
  border-radius: 6px;
  background: #fff7ed;
  color: #c2410c;
  font-size: 11.5px;
}
.ps-note--info {
  background: var(--ps-primary-soft);
  color: var(--ps-text-2);
  align-items: flex-start;
  line-height: 1.6;
}

/* Inputs */
.ps-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  color: var(--ps-muted);
}
.ps-field input,
.ps-field select {
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--ps-border-2);
  border-radius: 6px;
  background: var(--ps-surface);
  color: var(--ps-text);
  font: inherit;
  font-size: 12px;
  outline: none;
  width: 100%;
}
.ps-field input:focus,
.ps-field select:focus {
  border-color: var(--ps-primary);
}
.ps-row2,
.ps-grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.ps-row4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}
.ps-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ps-chips button {
  height: 28px;
  min-width: 46px;
  padding: 0 10px;
  border: 1px solid var(--ps-border-2);
  border-radius: 6px;
  background: var(--ps-surface);
  color: var(--ps-text-2);
  font-size: 12px;
  cursor: pointer;
}
.ps-chips button.on {
  border-color: var(--ps-primary);
  background: var(--ps-primary-soft);
  color: var(--ps-primary);
  font-weight: 600;
}
.ps-orient {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.ps-orient button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 56px;
  border: 1px solid var(--ps-border-2);
  border-radius: 6px;
  background: var(--ps-surface);
  color: var(--ps-text-2);
  cursor: pointer;
}
.ps-orient button.on {
  border-color: var(--ps-primary);
  background: var(--ps-primary-soft);
  color: var(--ps-primary);
  font-weight: 600;
}
.ps-sheet {
  width: 22px;
  height: 30px;
  border: 1.5px solid currentColor;
  border-radius: 2px;
}
.ps-sheet--land {
  width: 30px;
  height: 22px;
}
.ps-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--ps-text-2);
  cursor: pointer;
  user-select: none;
}
.ps-toggle input {
  display: none;
}
.ps-toggle i {
  position: relative;
  flex: 0 0 auto;
  width: 30px;
  height: 17px;
  border-radius: 17px;
  background: var(--ps-border-2);
  transition: background 0.15s;
}
.ps-toggle i::after {
  content: "";
  position: absolute;
  top: 2px;
  right: 2px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #fff;
  transition: right 0.15s;
}
.ps-toggle input:checked + i {
  background: var(--ps-primary);
}
.ps-toggle input:checked + i::after {
  right: 15px;
}
.ps-toggle--head {
  font-weight: 700;
}
.ps-range {
  width: 100%;
  accent-color: var(--ps-primary);
}
.ps-swatches {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.ps-swatches button {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px var(--ps-border-2);
  cursor: pointer;
}
.ps-swatches button.on {
  box-shadow: 0 0 0 2px var(--ps-text);
}
.ps-color {
  width: 30px;
  height: 26px;
  padding: 0;
  border: 1px solid var(--ps-border-2);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

/* Columns */
.ps-cols {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ps-col {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border: 1px solid var(--ps-border);
  border-radius: 6px;
  background: var(--ps-surface);
}
.ps-col.off {
  opacity: 0.55;
}
.ps-col.over {
  border-color: var(--ps-primary);
  box-shadow: 0 -2px 0 var(--ps-primary);
}
.ps-col__grip {
  cursor: grab;
  color: var(--ps-muted) !important;
}
.ps-col__label {
  flex: 1 1 auto;
  min-width: 0;
  height: 26px;
  padding: 0 6px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  font: inherit;
  font-size: 12px;
  color: var(--ps-text);
  outline: none;
}
.ps-col__label:focus {
  border-color: var(--ps-border-2);
  background: var(--ps-surface-2);
}
.ps-col__width {
  width: 70px;
  accent-color: var(--ps-primary);
}
.ps-check {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  border: 1.5px solid var(--ps-border-2);
  border-radius: 4px;
  background: var(--ps-surface);
  color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.ps-check.on {
  background: var(--ps-primary);
  border-color: var(--ps-primary);
  color: #fff;
}

.ps-small {
  font-size: 11px;
  line-height: 1.6;
}
.ps-icon--sm {
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}
.ps-toggle--hint span {
  display: flex;
  flex-direction: column;
  line-height: 1.4;
}
.ps-toggle--hint small {
  font-size: 10.5px;
  color: var(--ps-muted);
}
.ps-gallery {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}
.ps-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 4px;
  border: 1px solid var(--ps-border-2);
  border-radius: 6px;
  background: var(--ps-surface);
  color: var(--ps-text-2);
  font-size: 10.5px;
  cursor: pointer;
}
.ps-card:hover {
  border-color: var(--ps-primary);
  color: var(--ps-primary);
}
.ps-mini-head {
  width: 100%;
  height: 30px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 3px;
  padding: 3px;
  border-bottom: 2px solid var(--a);
  border-radius: 3px;
}
.ps-mini-head--band {
  background: var(--a);
  border: 0;
}
.ps-mini-head--boxed {
  border: 1px solid #cbd5e1;
  background: #f8fafc;
}
.ps-mini-head--plain {
  border-bottom: 1px solid #cbd5e1;
}
.ps-mini-head b {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}
.ps-mini-head .s-start {
  align-items: flex-start;
}
.ps-mini-head .s-center {
  align-items: center;
}
.ps-mini-head .s-end {
  align-items: flex-end;
}
.ps-mini-head i {
  display: block;
  height: 3px;
  border-radius: 2px;
  background: #94a3b8;
}
.ps-mini-head i.logo {
  width: 8px;
  height: 8px;
  background: #cbd5e1;
}
.ps-mini-head i.co {
  width: 18px;
  background: #475569;
}
.ps-mini-head i.ti {
  width: 22px;
  height: 4px;
  background: var(--a);
}
.ps-mini-head i.me {
  width: 14px;
}
.ps-mini-head--band i.ti,
.ps-mini-head--band i.co {
  background: #fff;
}
.ps-mini-table {
  width: 100%;
  display: grid;
  gap: 2px;
}
.ps-mini-table i {
  display: block;
  height: 5px;
  border-radius: 1px;
  background: #e2e8f0;
}
.ps-mini-table i:first-child {
  background: var(--a);
}
.ps-mini-table--grid i {
  background: #fff;
  outline: 1px solid #94a3b8;
}
.ps-mini-table--grid i:first-child,
.ps-mini-table--striped i:first-child {
  background: #dbe5f1;
}
.ps-mini-table--minimal i {
  background: transparent;
  border-bottom: 1px solid #cbd5e1;
}
.ps-mini-table--minimal i:first-child {
  background: transparent;
  border-bottom: 2px solid #334155;
}
.ps-mini-table--striped i:nth-child(odd) {
  background: #e8eef6;
}
.ps-mini-table--ledger i {
  outline: 1px solid #cbd5e1;
}
.ps-slotrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: var(--ps-text-2);
}
.ps-slotrow .ps-seg {
  height: 28px;
}
.ps-slotrow .ps-seg button {
  padding: 0 8px;
}
.ps-fonts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}
.ps-fonts button {
  height: 32px;
  border: 1px solid var(--ps-border-2);
  border-radius: 6px;
  background: var(--ps-surface);
  color: var(--ps-text-2);
  cursor: pointer;
}
.ps-fonts button.on {
  border-color: var(--ps-primary);
  background: var(--ps-primary-soft);
  color: var(--ps-primary);
  font-weight: 600;
}
/* Admin */
.ps-admin-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px 4px 2px;
  border: 1px solid var(--ps-border);
  border-radius: 6px;
}
.ps-admin-row .ps-ellipsis {
  flex: 1;
}
.ps-icon {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--ps-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.ps-icon:hover {
  background: var(--ps-primary-soft);
  color: var(--ps-primary);
}
.ps-icon.on {
  color: #d97706;
}
.ps-icon--danger:hover {
  background: #fef2f2;
  color: #dc2626;
}

/* Stage */
.ps-stage {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.ps-stage__bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 38px;
  padding: 0 12px;
  font-size: 11.5px;
  color: var(--ps-muted);
}
.ps-stage__bar b {
  color: var(--ps-text);
}
.ps-zoom {
  min-width: 42px;
  text-align: center;
  color: var(--ps-text-2);
}
.ps-frame {
  flex: 1 1 auto;
  width: 100%;
  border: 0;
  background: var(--ps-bg);
}
.ps-progress--ok {
  color: #15803d;
  background: #f0fdf4;
}
.ps-menu-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  max-width: 260px;
  padding: 6px 16px 8px;
  font-size: 11px;
  line-height: 1.6;
  color: #64748b;
}

/* Name / confirm dialog */
.ps-nd {
  --ps-surface: #fff;
  --ps-surface-2: #f8fafc;
  --ps-border: #e2e8f0;
  --ps-border-2: #cbd5e1;
  --ps-text: #0f172a;
  --ps-text-2: #334155;
  --ps-muted: #64748b;
  background: var(--ps-surface);
  color: var(--ps-text);
  border-radius: 6px;
  overflow: hidden;
  font-size: 12.5px;
}
.ps-nd .v-icon {
  color: inherit;
}
.ps-nd__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 6px;
}
.ps-nd__icon {
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  border-radius: 6px;
  background: var(--ps-primary-soft);
  color: var(--ps-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ps-nd__icon--danger {
  background: #fef2f2;
  color: #dc2626;
}
.ps-nd__title {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
}
.ps-nd__text {
  margin-top: 2px;
  color: var(--ps-muted);
  line-height: 1.7;
}
.ps-nd__x {
  margin-inline-start: auto;
}
.ps-nd__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 10px 16px 4px;
}
.ps-nd__field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.ps-nd__label {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  color: var(--ps-text-2);
}
.ps-nd__label small {
  font-weight: 400;
  color: var(--ps-muted);
}
.ps-nd__field input {
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--ps-border-2);
  border-radius: 6px;
  background: var(--ps-surface);
  color: var(--ps-text);
  font: inherit;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.ps-nd__field input:focus {
  border-color: var(--ps-primary);
  box-shadow: 0 0 0 3px var(--ps-primary-soft);
}
.ps-nd__field.is-error input {
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
}
.ps-nd__error {
  font-size: 11px;
  color: #dc2626;
}
.ps-nd__warn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #b45309;
}
.ps-nd__scopes {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ps-nd__scope {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border: 1px solid var(--ps-border-2);
  border-radius: 6px;
  background: var(--ps-surface);
  color: var(--ps-text-2);
  text-align: start;
  cursor: pointer;
}
.ps-nd__scope span {
  flex: 1;
  display: flex;
  flex-direction: column;
  line-height: 1.5;
}
.ps-nd__scope small {
  color: var(--ps-muted);
  font-size: 11px;
}
.ps-nd__scope.on {
  border-color: var(--ps-primary);
  background: var(--ps-primary-soft);
  color: var(--ps-primary);
}
.ps-nd__radio {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid var(--ps-border-2);
  background: #fff;
}
.ps-nd__scope.on .ps-nd__radio {
  border: 5px solid var(--ps-primary);
}
.ps-nd__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ps-nd__summary span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 8px;
  border-radius: 6px;
  background: var(--ps-surface-2);
  border: 1px solid var(--ps-border);
  color: var(--ps-muted);
  font-size: 11px;
}
.ps-nd__swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.ps-nd__foot {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 14px;
  padding: 12px 16px;
  background: var(--ps-surface-2);
  border-top: 1px solid var(--ps-border);
}
.ps-nd__foot .ps-btn {
  min-width: 96px;
  height: 36px;
}
.ps-btn--danger {
  background: #dc2626;
  border-color: #dc2626;
  color: #fff;
  font-weight: 600;
}
.ps-btn--danger:hover:not(:disabled) {
  color: #fff;
  border-color: #dc2626;
  filter: brightness(1.08);
}
</style>
